import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEpeQueries } from '../../../../config/react-query/epe.query';
import { useManoeuvresQueries } from '../../../../config/react-query/manoeuvre.query';
import { Button } from '../../../core';
import { getDriveLogById } from '../../../drive-log/drive-log.services';
import {
	deleteFileAttachmentByIdInObs,
	getFilesAttachmentByObs,
	insertObservationFileAttachment,
} from '../../../drive-log/file-attachment.services';
import {
	addObservation,
	sendEpe,
	sendManoeuvre,
	updateObservationById,
} from '../../observation.services';
import findFilesToAddOrDelete from '../../utils/observation-file-attachment/find-files-to-add-or-delete';
import './observation-form.scss';
import ObservationModal from './observation-modal/observation-modal';
import ObservationRootField from './observation-root-field/observation-root-field';

const ObservationForm = (props) => {
	const {
		observationData,
		observationFields,
		observationType,
		linkedEntity,
		redirectUrl = '',
	} = props;

	
	const params = useParams();
	const driveLogId = params.driveLogId || params.id;
	const missionId = params.missionId;

	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const type = searchParams.get('type');
	const [driveLog, setDriveLog] = useState({});
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [line, setLine] = useState();
	const [fields, setFields] = useState({});
	const currentDate = new Date();
	const [formValid, setFormValid] = useState(false);
	const [timeError, setTimeError] = useState(false);
	const [modalActive, setModalActive] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [observationFiles, setObservationFiles] = useState([]);
	const [currentObsFiles, setCurrentObsFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const { addManoeuvre, updateManoeuvre } = useManoeuvresQueries();
	const { addEpe, updateEpe } = useEpeQueries();
	const [showMessage, setShowMessage] = useState(false);

	const dlUrl = `${redirectUrl}/${driveLogId}`;
	const authorizationUrl = `${redirectUrl}/${driveLogId}/mission/${missionId}/observation/add-move-signal-authorization`;

	const signalObservation = ['installation', 'cleanness', 'material'];
	const isSignalObservation = signalObservation.includes(observationType);

	const disabledSubmitButton = !formValid && observationType === 'manual-drive';

	/**
	 * load old data
	 */
	
	useEffect(() => {
	if (observationData?.content) {
			const patchedContent = { ...observationData.content };
		
			
			if (typeof patchedContent.trainStorageChoice === 'string') {
				patchedContent.trainStorageChoice = {
					key: patchedContent.trainStorageChoice,
					name: patchedContent.trainStorageChoice,
					line: driveLog?.line_number ?? '',
				};
			} else if (
				patchedContent.trainStorageChoice &&
				typeof patchedContent.trainStorageChoice === 'object' &&
				typeof patchedContent.trainStorageChoice.name === 'string'
			) {
				patchedContent.trainStorageChoice = {
					key: patchedContent.trainStorageChoice.name,
					name: patchedContent.trainStorageChoice.name,
					line: driveLog?.line_number ?? '',
				};
			}

			setFields(patchedContent);
		}
	}, [observationData, driveLog]);


	/**
	 * load default values
	 */
	useEffect(() => {
		if (!observationData?.content) {
			const defaultValues = observationFields.map((fieldSchema) => {
				const { fieldName, defaultValue, group } = fieldSchema;
				if (group && group.length > 0) {
					const groupDefaultValues = group.map((childSchema) => {
						const { fieldName: groupName, defaultValue: groupDefaultValue } = childSchema;
						if (groupDefaultValue) {
							return [groupName, groupDefaultValue({ driveLog })];
						}
						return null;
					});
					const defaultValues = groupDefaultValues.filter((value) => value !== null);
					if (defaultValues) {
						return defaultValues;
					}
				}
				if (defaultValue) {
					return [fieldName, defaultValue({ driveLog })];
				}
				return [fieldName];
			});
			const flattenedDefaultValues = defaultValues
				.flatMap((value) => (Array.isArray(value[0]) ? value : [value]))
				.filter(([, defaultValue]) => {
					return !!defaultValue;
				});
			if (flattenedDefaultValues?.length > 0) {
				const defaultFields = Object.fromEntries(flattenedDefaultValues);
				setFields(defaultFields);
			}
		}
	}, [observationFields, observationData, driveLog]);

	/**
	 * Load drive log
	 */
	useEffect(() => {
		getDriveLogById(driveLogId)
			.then((res) => {
				setLine(res?.data?.line_number);
				setDriveLog(res?.data);
			})
			.catch((err) => {
				console.error(err);
			});
	}, [driveLogId]);




	/**
	 * if a line array exists, check if it includes current line
	 * @param {Array} lines
	 * @return {boolean}
	 */
	const lineMatches = useCallback(
		(lines) => {
			return !lines || lines.includes(line);
		},
		[line],
	);

	/**
	 * check if child field should be shown or not
	 * @param {string} fieldName
	 * @param {Object} childSchema
	 * @return {boolean}
	 */
	const isChildShown = useCallback(
		(fieldName, childSchema) => {
			const { parentValue, parentTranslated, lines } = childSchema;
			if (!lineMatches(lines)) {
				return false;
			}

			const parentStateValue = fields[fieldName]?.value ?? fields[fieldName];
			const matchedValue = parentTranslated
				? t(`observation:observation-fields.${fieldName}-values.${parentValue}`)
				: parentValue;

			// child depends on value of the parent field
			return parentStateValue === matchedValue;
		},
		[fields, lineMatches, t],
	);

	/**
	 * check if parent field is valid
	 * @return {boolean}
	 */
	const isParentFieldValid = useCallback(
		(fieldName, mandatory, lines, validator) => {

			const fieldSchema = observationFields.find((f) => f.fieldName === fieldName);

			// if not visible, field is not valid
			if (fieldSchema?.visibleIf && !fieldSchema.visibleIf(fields)) {
				return true;
			}

			// field valid if not shown, if not mandatory and empty
			if (!lineMatches(lines) || (!mandatory && !fields[fieldName])) {
				return true;
			}

			// if no validator, field valid if not mandatory or if mandatory and has a value
			if (!validator) {
				return !mandatory || fields[fieldName];
			}

			// If mandatory, check if not undefined or empty
			if (mandatory && (fields[fieldName] === undefined || fields[fieldName] === '')) {
				return false;
			}
			
			  

			return validator(fields[fieldName]);
		},
		[fields, lineMatches, observationFields],
	);

	/**
	 * check if grouped field is valid
	 * @return {boolean}
	 */
	const isGroupedFieldValid = useCallback(
		(fieldSchema, groupMandatory) => {
			const { group } = fieldSchema;

			   const effectiveGroup = group.filter((groupedField) => {
				if (typeof groupedField.visibleIf === 'function') {
				  return groupedField.visibleIf(fields);
				}
				return true;
			  });

			const groupedFieldsNotValid = effectiveGroup.some((groupedField) => {
				const { fieldName, mandatory, validator, lines } = groupedField;
				return !isParentFieldValid(fieldName, mandatory, lines, validator);
			});

			// if group is not mandatory then it's valid if all children are valid
			if (!groupMandatory) {
				
				return !groupedFieldsNotValid;
			}

			// if group is mandatory then it has at least one child with a value and all children are valid
			const groupHasValue = effectiveGroup.some((groupedField) => fields[groupedField.fieldName]);
			return groupHasValue && !groupedFieldsNotValid;
		},
		[fields, isParentFieldValid],
	);

	const isRepeatFieldValid = useCallback(
		(fieldSchema, mandatory) => {
			const { fieldName, validator, lines } = fieldSchema;

			// field valid if not shown, if not mandatory and empty
			if (!lineMatches(lines) || (!mandatory && !fields[fieldName])) {
				return true;
			}

			// field valid if at least one field is valid ( not undefined, not null )
			if (fields[fieldName]?.some((fieldValue) => !!fieldValue)) {
				return true;
			}

			// field not valid if mandatory and doesn't have any values
			if (mandatory && !fields[fieldName]?.length > 0) {
				return false;
			}

			// field not valid if it has non valid repeater fields
			const repeatFieldNotValid = fields[fieldName].some((fieldValue) => {
				// if no validator, field not valid if mandatory and doesn't have a value
				if (!validator) {
					return mandatory && !fieldValue;
				}

				return !validator(fieldValue);
			});

			return !repeatFieldNotValid;
		},
		[fields, lineMatches],
	);

	/**
	 * check if child field is valid
	 * @return {boolean}
	 */
	const isChildFieldValid = useCallback(
		(fieldName, childSchema) => {
			const { fieldName: childName, mandatory, validator, modalTriggerValue } = childSchema;

			const childValue = fields[fieldName]?.children[childName];

			// child valid if not shown
			if (!isChildShown(fieldName, childSchema)) {
				return true;
			}
			// if child is shown, check if modal needs to be triggered on submit
			if (modalTriggerValue && modalTriggerValue === childValue) {
				setModalActive(true);
			}

			// child valid if mandatory and empty
			if (!isChildShown(fieldName, childSchema) || (!mandatory && !childValue)) {
				return true;
			}

			// if no validator, child valid if not mandatory or mandatory and has a value
			if (!validator) {
				return !mandatory || childValue;
			}

			return validator(childValue);
		},
		[fields, isChildShown],
	);

	/**
	 * check if form is valid
	 */
	useEffect(() => {
		const fieldsNotValid = observationFields.filter((fieldSchema) => {
			const {
				fieldName,
				children,
				group,
				mandatory: defaultMandatory,
				lines,
				validator,
				repeat,
				visibleOnSide,
			} = fieldSchema;

			const childrenNotValid =
				children && children.some((childSchema) => !isChildFieldValid(fieldName, childSchema));

			const mandatory = visibleOnSide
				? visibleOnSide.includes(linkedEntity) && defaultMandatory
				: defaultMandatory;

			let isFieldValid;

			if (group) {
				isFieldValid = isGroupedFieldValid(fieldSchema, mandatory);
			} else if (repeat) {
				isFieldValid = isRepeatFieldValid(fieldSchema, mandatory);
			} else {
				isFieldValid = isParentFieldValid(fieldName, mandatory, lines, validator);
			}

			return !isFieldValid || childrenNotValid;
		});

		setFormValid(fieldsNotValid?.length === 0);
	}, [
		observationFields,
		isChildFieldValid,
		isParentFieldValid,
		isGroupedFieldValid,
		isRepeatFieldValid,
		linkedEntity,
	]);

	const formReset = () => {
		setFields({});
		setFormValid(false);
		setModalActive(false);
		setShowModal(false);
	};

	/**
	 * Load observation files if needed
	 */
	useEffect(() => {
		if (isSignalObservation) {
			getFilesAttachmentByObs(driveLogId, observationData?.id)
				.then((res) => {
					const { data } = res;
					setCurrentObsFiles(data);
				})
				.catch((err) => {
					console.error(err);
				});
		}
	}, [driveLogId, observationData?.id, isSignalObservation]);

	const updateObservationFiles = () => {
		const filesToAddOrDelete = findFilesToAddOrDelete(observationFiles, currentObsFiles);
		// delete the files that have to be deleted when updating the observation
		const { addedFiles, deletedFiles } = filesToAddOrDelete;
		if (deletedFiles?.length > 0) {
			const filesIdsToDelete = deletedFiles.map((file) => file.id);
			return deleteFileAttachmentByIdInObs(driveLogId, filesIdsToDelete, observationData.id).catch(
				(err) => {
					console.error(err);
				},
			);
		}
		// insert the files that have been added when updating the observation
		if (addedFiles?.length > 0) {
			insertObservationFileAttachment(addedFiles, driveLogId, observationData.id).catch((err) => {
				console.error(err);
			});
		}
		return null;
	};

	
		
const submitForm = (formRedirectUrl) => {
		setShowMessage(false);
		setLoading(true);


		const draft = !formValid || timeError;
		// filter out empty or undefined parents
		const formatFieldsArr = Object.entries(fields).filter(([, fieldValue]) => !!fieldValue);
	
		const observation = {
			observation_type: observationType,
			creation_date: currentDate.toISOString(),
			content: Object.fromEntries(formatFieldsArr),
		};
	

		if (missionId) {
			observation.mission_id = missionId;
		}
		

		const afterSubmit = async () => {
			if (isSignalObservation) await updateObservationFiles();
			formReset();
			navigate(formRedirectUrl);
		};

		if (type === 'epe' && observationData?.id) {
			const epePayload = {
				train_number: observation.content.cabin,
				station_start: observation.content.trainStorageChoice.name,
				cassette_noted: observation.content.cassetteNoted,
				cassette_installed: observation.content.cassetteInstalled || null,
				sens: observation.content.sens,
				period: observation.content.period,
				hour: observation.content.dateTime,
				comment: observation.content.comment || null,
				material_type: observation.content.materialType,
				creation_date: observation.creation_date,
			};
      
		

			updateEpe.mutate(
				{
					driveLogId,
					epeId: observationData.id,
					values: epePayload,
					action: afterSubmit,
				},
				{
					onError: (err) => {
						console.error('EPE update failed:', err);
						setLoading(false);
					},
				},
			);
		} else if (type) {
			if (draft) {
				setShowMessage(true);
				setLoading(false);
			} else {
				if (type === 'tnms') {
					sendManoeuvre(
						driveLogId,
						observation.content,
						observationData?.content?.id,
						observation.creation_date,
						observationData ? updateManoeuvre : addManoeuvre,
						afterSubmit,
					);
				} else {
					sendEpe(
						driveLogId,
						observation.content,
						observationData?.content?.id,
						observation.creation_date,
						observationData ? updateEpe : addEpe,
						afterSubmit,
					);
				}
			}
		} else if (observationData) {
  if (observationType === 'releve_epe') {
    updateObservationById(driveLogId, observationData.id, observation, draft)
      .then(afterSubmit)
      .catch(console.error)
      .finally(() => setLoading(false));
  } else {
    updateObservationById(driveLogId, observationData.id, observation, draft)
      .then(async (res) => {
        if (isSignalObservation) await updateObservationFiles();
        formReset();
        navigate(formRedirectUrl);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }
} else {
  addObservation(driveLogId, observation, draft)
    .then(async (res) => {
      if (observationType !== 'releve_epe' && observationFiles.length !== 0 && res) {
        const { data: createdObservationId } = res;
        await insertObservationFileAttachment(
          observationFiles,
          driveLogId,
          createdObservationId,
        ).catch(console.error);
      }
      formReset();
      navigate(formRedirectUrl);
    })
    .catch(console.error)
    .finally(() => setLoading(false));
}

	};


	const confirmCallback = () => {
		submitForm(dlUrl);
	};

	const cancelCallback = () => {
		submitForm(authorizationUrl);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		
		if (!modalActive) {
			submitForm(dlUrl);
		} else {
			setShowModal(true);
		}
	};

	const filteredObservationFields = observationFields.filter((obsField) => {
		if (type === 'tnms' && obsField.disableTnMs) {
			return false;
		}
		return !obsField?.visibleOnSide || obsField?.visibleOnSide?.includes(linkedEntity);
	});

	const handleFieldChange = (fieldName) => (e) => {
		
		const value = e.target.value;
	
		setFields((prev) => ({
		  ...prev,
		  
		  [fieldName]: value,
		}));
	};


	// some fields must only be displayed on one side - a side is related to an entity : mission or drive-log
	const renderObservationFields = () =>
		filteredObservationFields.map((fieldSchema) => (
			<ObservationRootField
				key={fieldSchema.fieldName}
				line={line}
				fieldSchema={fieldSchema}
				fields={fields}
				observationFields={observationFields}
				setFields={setFields}
				isChildShown={isChildShown}
				setTimeError={setTimeError}
				driveLog={driveLog}
				observationFiles={observationFiles}
				setObservationFiles={setObservationFiles}
				observationId={observationData?.id}
				allFields={fields}
				handleChange={handleFieldChange(fieldSchema.fieldName)}
			/>

		));
	

	return (
		<form className="observation-form" onSubmit={handleSubmit}>
			{line && renderObservationFields()}
			{showMessage && (
				<p className="observation-form__error-message">
					{t(`observation:observation-error-message-${type}`)}
				</p>
			)}
			<Button
				className="observation-form__validate-button button"
				type="submit"
				disabled={disabledSubmitButton || loading}
			>
				{t('observation:observation-fields.validate-button')}
				{loading && <div className={'spinner'} />}
			</Button>

			<ObservationModal
				showModal={showModal}
				toggleModal={setShowModal}
				confirmCallback={confirmCallback}
				cancelCallback={cancelCallback}
				modalText={t(`observation:observation-modals.${observationType}`)}
			/>
		</form>
	);
};

export default ObservationForm;
